import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import { useCart } from '../../context/CartContext';
import api from '../../utils/api';

// ─────────────────────────────────────────────────────────────────
// ORDER SUCCESS
// ─────────────────────────────────────────────────────────────────
export function OrderSuccess() {
  const [sp] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const orderNum = sp.get('order');

  useEffect(() => {
    clearCart();
    const sessionId = sp.get('session_id');
    if (sessionId) {
      api.get(`/payments/session/${sessionId}`).then(r => setOrder(r.data.order)).catch(()=>{});
    }
  }, []);

  return (
    <>
      <Helmet><title>Order Confirmed | Sudath Gems</title></Helmet>
      <Navbar />
      <div style={{ paddingTop:72, minHeight:'80vh', display:'flex', alignItems:'center' }}>
        <div className="container" style={{ padding:'80px 40px', textAlign:'center' }}>
          <div style={{ width:80, height:80, borderRadius:'50%', border:'2px solid var(--gold)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 32px', fontSize:36 }}>
            ✓
          </div>
          <span style={{ fontSize:11, letterSpacing:3, textTransform:'uppercase', color:'var(--gold)', display:'block', marginBottom:16 }}>Order Confirmed</span>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(2rem,5vw,3.5rem)', fontWeight:300, marginBottom:16 }}>Thank You for Your Order</h1>
          <div className="gold-divider" style={{ marginBottom:24 }} />
          <p style={{ fontSize:15, color:'var(--muted)', maxWidth:520, margin:'0 auto 12px', lineHeight:1.8 }}>
            Your order <strong style={{ color:'var(--gold)' }}>{orderNum}</strong> has been confirmed and payment received. Our team will process and dispatch your gemstone with full insurance and tracking.
          </p>
          <p style={{ fontSize:13, color:'var(--muted2)', maxWidth:480, margin:'0 auto 48px', lineHeight:1.7 }}>
            A confirmation email will arrive shortly. For any questions, contact us at <a href="mailto:info@ceylongems.com" style={{ color:'var(--gold)' }}>info@ceylongems.com</a>
          </p>
          <div style={{ display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/shop" className="btn btn-gold btn-lg">Continue Shopping</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Contact Us</Link>
          </div>
          <div style={{ marginTop:60, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:24, maxWidth:640, marginInline:'auto' }}>
            {[['📦','Processing','Your gems are being carefully packaged'],['✈️','Shipping','Fully insured international delivery'],['📬','Delivery','Tracking number sent by email']].map(([icon,title,desc])=>(
              <div key={title} style={{ padding:24, border:'1px solid var(--border)', borderRadius:8, background:'var(--surface)' }}>
                <div style={{ fontSize:28, marginBottom:12 }}>{icon}</div>
                <p style={{ fontSize:13, color:'var(--gold)', fontWeight:500, marginBottom:6 }}>{title}</p>
                <p style={{ fontSize:12, color:'var(--muted)', lineHeight:1.5 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// ABOUT PAGE
// ─────────────────────────────────────────────────────────────────
export function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Sudath Gems | Our Heritage & Story</title>
        <meta name="description" content="Sudath Gems — trusted purveyors of authentic Sri Lankan gemstones for over 35 years. GIA certified, ethically sourced, worldwide delivery." />
      </Helmet>
      <Navbar />
      <div style={{ paddingTop:72 }}>
        {/* Hero */}
        <div style={{ position:'relative', height:480, overflow:'hidden' }}>
          <img src="https://images.unsplash.com/photo-1578301978693-85fa9c0320b9?w=1800&q=85" alt="Sri Lanka mines"
            style={{ width:'100%', height:'100%', objectFit:'cover', filter:'brightness(0.35)' }} />
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', textAlign:'center', padding:20 }}>
            <span style={{ fontSize:11, letterSpacing:4, textTransform:'uppercase', color:'var(--gold)', display:'block', marginBottom:16 }}>Est. 1985 · Ratnapura, Sri Lanka</span>
            <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(2.5rem,6vw,5rem)', fontWeight:300, color:'var(--white)', marginBottom:16 }}>Our Story</h1>
            <div className="gold-divider" />
          </div>
        </div>

        <section className="section">
          <div className="container-sm">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:72, alignItems:'center', marginBottom:80 }}>
              <div>
                <span style={{ fontSize:11, letterSpacing:3, textTransform:'uppercase', color:'var(--gold)', display:'block', marginBottom:16 }}>Heritage</span>
                <h2 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(1.8rem,3vw,2.5rem)', fontWeight:300, marginBottom:20, lineHeight:1.3 }}>Three Generations of Gem Expertise</h2>
                <div style={{ width:50, height:1, background:'var(--gold)', marginBottom:24 }} />
                <p style={{ fontSize:14, color:'var(--muted)', lineHeight:1.9, marginBottom:16 }}>
                  Founded in 1985 by master gemologist S.D. Perera in the heart of Ratnapura — Sri Lanka's legendary "City of Gems" — Sudath Gems has grown from a small family-run mine to one of the island's most trusted gemstone exporters.
                </p>
                <p style={{ fontSize:14, color:'var(--muted)', lineHeight:1.9 }}>
                  Today, three generations of the Perera family continue the tradition of selecting only the finest natural, unheated gemstones from our own licensed mining operations and trusted partner mines across the country.
                </p>
              </div>
              <img src="https://images.unsplash.com/photo-1617038220319-276d3cfab638?w=900&q=85" alt="Gem cutting"
                style={{ width:'100%', height:400, objectFit:'cover', borderRadius:4, filter:'brightness(0.9)' }} />
            </div>

            {/* Values */}
            <div style={{ textAlign:'center', marginBottom:60 }}>
              <h2 style={{ fontFamily:'var(--font-serif)', fontSize:36, fontWeight:300, marginBottom:16 }}>Our Commitments</h2>
              <div className="gold-divider" style={{ marginBottom:48 }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:32, marginBottom:80 }}>
              {[
                { icon:'🔬', title:'Scientific Grading', desc:'All gemstones are independently verified by GIA, AGL, or GRS laboratories before being offered for sale.' },
                { icon:'🌿', title:'Ethical Mining', desc:'We operate licensed, regulated mines and pay Sri Lanka\'s highest wages to our miners and cutters.' },
                { icon:'🌍', title:'Global Trust', desc:'Collectors in 60+ countries have trusted us with their most important gem purchases over 35 years.' },
              ].map(({ icon, title, desc }) => (
                <div key={title} style={{ textAlign:'center', padding:32, border:'1px solid var(--border)', borderRadius:8, background:'var(--surface)' }}>
                  <div style={{ fontSize:36, marginBottom:16 }}>{icon}</div>
                  <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, marginBottom:12 }}>{title}</h3>
                  <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.8 }}>{desc}</p>
                </div>
              ))}
            </div>

            {/* Shipping */}
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:48 }}>
              <h2 style={{ fontFamily:'var(--font-serif)', fontSize:28, fontWeight:300, marginBottom:24 }}>Shipping & Delivery</h2>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32 }}>
                {[
                  { title:'Standard International', time:'7–14 business days', cost:'$25 USD (Free over $500)', details:'Tracked, insured, with customs documentation.' },
                  { title:'Express International', time:'3–5 business days', cost:'$55 USD', details:'DHL Express. Fully insured. Signature required on delivery.' },
                ].map(s => (
                  <div key={s.title} style={{ padding:24, border:'1px solid var(--border2)', borderRadius:4 }}>
                    <h4 style={{ fontFamily:'var(--font-serif)', fontSize:18, fontWeight:400, color:'var(--gold)', marginBottom:8 }}>{s.title}</h4>
                    <p style={{ fontSize:13, color:'var(--white)', marginBottom:4 }}>⏱ {s.time}</p>
                    <p style={{ fontSize:13, color:'var(--gold)', marginBottom:8 }}>💵 {s.cost}</p>
                    <p style={{ fontSize:13, color:'var(--muted)' }}>{s.details}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// CONTACT PAGE
// ─────────────────────────────────────────────────────────────────
export function ContactPage() {
  const [form, setForm] = useState({ name:'', email:'', phone:'', country:'', subject:'', message:'' });
  const [loading, setLoading] = useState(false);
  const f = k => e => setForm({...form, [k]: e.target.value});

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post('/enquiries', form);
      toast.success('Message sent! We\'ll respond within 24 hours.');
      setForm({ name:'', email:'', phone:'', country:'', subject:'', message:'' });
    } catch { toast.error('Failed to send. Please email us directly.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Helmet>
        <title>Contact Sudath Gems | Expert Gemstone Enquiries</title>
        <meta name="description" content="Contact our expert gemologists for inquiries about natural Ceylon gemstones, custom orders, and worldwide shipping." />
      </Helmet>
      <Navbar />
      <div style={{ paddingTop:72 }}>
        <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'60px 0 40px' }}>
          <div className="container">
            <span style={{ fontSize:11, letterSpacing:3, textTransform:'uppercase', color:'var(--gold)', display:'block', marginBottom:12 }}>Get in Touch</span>
            <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(2rem,5vw,3.5rem)', fontWeight:300 }}>Contact Our Gemologists</h1>
          </div>
        </div>

        <section className="section">
          <div className="container">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1.5fr', gap:72 }}>
              <div>
                <h2 style={{ fontFamily:'var(--font-serif)', fontSize:28, fontWeight:300, marginBottom:20 }}>We'd Love to Hear From You</h2>
                <div style={{ width:50, height:1, background:'var(--gold)', marginBottom:24 }} />
                <p style={{ fontSize:14, color:'var(--muted)', lineHeight:1.9, marginBottom:36 }}>
                  Whether you're looking for a specific gem, seeking advice on an investment, or exploring custom orders — our master gemologists are here to help. We respond to all enquiries within 24 hours.
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
                  {[
                    { icon:'📍', label:'Address', val:'Jewellery Quarter, Ratnapura\nSabaragamuwa Province, Sri Lanka' },
                    { icon:'✉️', label:'Email', val:'info@ceylongems.com' },
                    { icon:'📞', label:'Phone', val:'+94 77 123 4567' },
                    { icon:'💬', label:'WhatsApp', val:'+94 77 123 4567' },
                    { icon:'🕐', label:'Response Time', val:'Within 24 hours' },
                  ].map(({ icon, label, val }) => (
                    <div key={label} style={{ display:'flex', gap:16 }}>
                      <div style={{ width:44, height:44, borderRadius:'50%', border:'1px solid var(--border2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{icon}</div>
                      <div>
                        <p style={{ fontSize:11, letterSpacing:1.5, textTransform:'uppercase', color:'var(--gold)', marginBottom:4 }}>{label}</p>
                        <p style={{ fontSize:13, color:'var(--white)', whiteSpace:'pre-line' }}>{val}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:40 }}>
                <h3 style={{ fontFamily:'var(--font-serif)', fontSize:22, fontWeight:400, marginBottom:24 }}>Send a Message</h3>
                <form onSubmit={handleSubmit}>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <input required value={form.name} onChange={f('name')} className="form-input" placeholder="Your full name" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input required type="email" value={form.email} onChange={f('email')} className="form-input" placeholder="your@email.com" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input value={form.phone} onChange={f('phone')} className="form-input" placeholder="+1 234 567 8900" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <input value={form.country} onChange={f('country')} className="form-input" placeholder="United States" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <select value={form.subject} onChange={f('subject')} className="form-select">
                      <option value="">Select a subject</option>
                      <option>General Enquiry</option>
                      <option>Specific Gem Request</option>
                      <option>Certification Questions</option>
                      <option>Custom Order</option>
                      <option>Wholesale Enquiry</option>
                      <option>Shipping & Delivery</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message *</label>
                    <textarea required value={form.message} onChange={f('message')} className="form-input form-textarea" placeholder="Please describe what you're looking for..." style={{ minHeight:140 }} />
                  </div>
                  <button type="submit" disabled={loading} className="btn btn-gold btn-lg" style={{ width:'100%', justifyContent:'center' }}>
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default OrderSuccess;
